import { Button, CircularProgress, Grid } from "@material-ui/core";
import * as React from "react";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { useEmailTopicsContext } from "../../context/EmailTopicsContext";
import {
	RECIPIENT_TYPE,
	type RecipientType,
} from "../../models/EmailAddressTopic";
import {
	EmailTopicFormValues,
	INITIAL_VALUES,
} from "../../models/FormInitialValues";
import { upsertEmailTopic } from "../../repositories/EmailTopicsRepository";
import { EmailTopicFormFields } from "./EmailTopicFormFields";

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

const toDelay = (value: string) =>
	value === "" || value === null ? null : Number(value);

export const EditEmailTopic = ({ show, onHide }: Props) => {
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");
	const {
		state: { topicSelected, addresses, addressTopics },
	} = useEmailTopicsContext();

	const { control, handleSubmit, reset } = useForm<EmailTopicFormValues>({
		defaultValues: INITIAL_VALUES,
	});

	const onSubmit: SubmitHandler<EmailTopicFormValues> = async (data) => {
		if (!topicSelected) return;

		setIsSubmitLoading(true);
		try {
			await upsertEmailTopic({
				EmailTopicId: topicSelected.EmailTopicId,
				EmailTopicName: data.EmailTopicName,
				Description: data.Description || null,
				DelayTimeMinutes: toDelay(data.DelayTimeMinutes),
				IsActive: data.IsActive,
				AddressIdsTO: data.AddressIdsTO,
				AddressIdsCC: data.AddressIdsCC,
				AddressIdsBCC: data.AddressIdsBCC,
			});
			onHide(true);
		} catch (e: any) {
			setError(e?.message || "Error saving email topic");
		} finally {
			setIsSubmitLoading(false);
		}
	};

	useEffect(() => {
		if (!show || !topicSelected) return;

		const idsFor = (type: RecipientType) =>
			addressTopics
				.filter(
					(assignment) =>
						assignment.EmailTopicId === topicSelected.EmailTopicId &&
						assignment.RecipientType === type,
				)
				.map((assignment) => assignment.EmailAddressId);

		reset({
			EmailTopicId: topicSelected.EmailTopicId,
			EmailTopicName: topicSelected.EmailTopicName,
			Description: topicSelected.Description ?? "",
			DelayTimeMinutes:
				topicSelected.DelayTimeMinutes === null
					? ""
					: String(topicSelected.DelayTimeMinutes),
			IsActive: topicSelected.IsActive,
			AddressIdsTO: idsFor(RECIPIENT_TYPE.TO),
			AddressIdsCC: idsFor(RECIPIENT_TYPE.CC),
			AddressIdsBCC: idsFor(RECIPIENT_TYPE.BCC),
		});
	}, [show, topicSelected, addressTopics]);

	return (
		<>
			<ErrorModal error={error} onHide={() => setError("")} />
			<MesfModal
				title="EDIT EMAIL TOPIC"
				open={show}
				handleClose={() => onHide(false)}
				id="edit-email-topic-modal"
				maxWidth="md"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content style={{ padding: "15px 30px 0" }}>
						<EmailTopicFormFields control={control} addresses={addresses} />
					</MesfModal.Content>
					<MesfModal.Actions>
						<Grid
							container
							spacing={2}
							justifyContent="flex-end"
							style={{ padding: "1rem" }}
						>
							<Grid item md={3} xs={12}>
								<Button
									fullWidth
									variant="contained"
									onClick={() => onHide(false)}
									disabled={isSubmitLoading}
								>
									CANCEL
								</Button>
							</Grid>
							<Grid item md={3} xs={12}>
								<Button
									fullWidth
									type="submit"
									variant="contained"
									color="primary"
									disabled={isSubmitLoading}
								>
									{isSubmitLoading ? <CircularProgress size={24} /> : "SAVE"}
								</Button>
							</Grid>
						</Grid>
					</MesfModal.Actions>
				</form>
			</MesfModal>
		</>
	);
};
